<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import { useProjectsStore } from '@/stores/projects'
  import { useUserStore } from '@/stores/user'
  import { closeListItemMenus } from '@/stores/list-item-menu'
  import { useMediaQuery } from '@vueuse/core'
  import { isAbortError } from '@/helpers/http'
  import type { TRecentItem } from '@/types/projects'
  import DesHeading from '@/components/DesHeading.vue'
  import DesTabs from '@/components/DesTabs.vue'
  import DesProjectList from '@/components/projects/DesProjectList.vue'
  import DesProject from '@/components/projects/DesProject.vue'
  import DesProjectContextMenu from '@/components/projects/DesProjectContextMenu.vue'
  import DesProjectAudiobook from '@/components/projects/DesProjectAudiobook.vue'
  import DesAudiobookContextMenu from '@/components/projects/DesAudiobookContextMenu.vue'
  import DesProjectFlipbook from '@/components/projects/DesProjectFlipbook.vue'
  import DesFlipbookContextMenu from '@/components/projects/DesFlipbookContextMenu.vue'
  import DesDraft from '@/components/drafts/DesDraft.vue'
  import DesDraftContextMenu from '@/components/drafts/DesDraftContextMenu.vue'
  import DesListCover from '@/components/cover/DesListCover.vue'
  import DesListCoverContextMenu from '@/components/cover/DesListCoverContextMenu.vue'
  import DesListPresentation from '@/components/presentation/DesListPresentation.vue'
  import DesListPresentationContextMenu from '@/components/presentation/DesListPresentationContextMenu.vue'
  import DesNoListElements from '@/components/DesNoListElements.vue'

  const projects = useProjectsStore()
  const user = useUserStore()
  const sharedList = ref<InstanceType<typeof DesProjectList> | null>(null)
  const isLoading = ref<boolean>(true)
  const isMobile = useMediaQuery('(max-width: 768px)')

  const activeKind = ref<string>('all')
  const kindTabs: { name: string; label: string }[] = [
    { name: 'ebook', label: 'eBooks' },
    { name: 'flipbook', label: 'Flipbooks' },
    { name: 'audiobook', label: 'Audiobooks' },
    { name: 'printbook', label: 'Print Books' },
    { name: 'kindlebook', label: 'Kindle Books' },
    { name: 'cover', label: 'Covers' },
    { name: 'presentation', label: 'Presentations' },
  ]

  const itemKind = (item: TRecentItem): string => {
    if (item.source === 'flipbook') return 'flipbook'
    if (item.source === 'audiobook') return 'audiobook'
    if (item.source === 'cover') return 'cover'
    if (item.source === 'presentation') return 'presentation'
    if (item.source === 'draft' && item.type === 'KindleBook') return 'kindlebook'
    if (item.source === 'draft' && item.type === 'PrintBook') return 'printbook'

    return 'ebook'
  }

  const recentItems = computed(() => projects.recentItems)

  const recentTabs = computed(() => {
    const kinds = new Set(recentItems.value.map(itemKind))

    return [{ name: 'all', label: 'All' }, ...kindTabs.filter((tab) => kinds.has(tab.name))]
  })

  const visibleItems = computed(() => {
    const items =
      activeKind.value === 'all'
        ? recentItems.value
        : recentItems.value.filter((item) => itemKind(item) === activeKind.value)

    if (isMobile.value) return items.slice(0, 10)

    return items.slice(0, (sharedList.value?.cols ?? 7) * 2)
  })

  watch(activeKind, () => {
    closeListItemMenus()
  })

  watch(recentTabs, (tabs) => {
    if (!tabs.some((tab) => tab.name === activeKind.value)) {
      activeKind.value = 'all'
    }
  })

  onMounted(() => {
    if (!user.isClientAccount()) {
      fetchRecentItems()
    }
  })

  const fetchRecentItems = async () => {
    try {
      isLoading.value = true
      await projects.fetchRecentItems()
    } catch (error: any) {
      if (isAbortError(error)) {
        return
      }
      throw error
    } finally {
      isLoading.value = false
    }
  }
</script>

<template>
  <div>
    <DesHeading level="4" class="mb-6">Recent projects</DesHeading>

    <DesTabs v-if="recentTabs.length > 1" v-model="activeKind" :list="recentTabs" class="mb-6" />

    <DesProjectList ref="sharedList">
      <template v-for="item in visibleItems" :key="`${item.source}-${item.id}`">
        <DesProject v-if="item.source === 'project'" :item="item" disable-checkboxes />
        <DesDraft v-else-if="item.source === 'draft'" :item="item" disable-checkboxes />
        <DesProjectAudiobook v-else-if="item.source === 'audiobook'" :item="item" disable-checkboxes />
        <DesProjectFlipbook v-else-if="item.source === 'flipbook'" :item="item" disable-checkboxes />
        <DesListCover v-else-if="item.source === 'cover'" :item="item" disable-checkboxes />
        <DesListPresentation v-else-if="item.source === 'presentation'" :item="item" disable-checkboxes />
      </template>
    </DesProjectList>

    <DesNoListElements
      v-if="!visibleItems.length && !isLoading"
      :itemName="'eBook'"
      :itemsName="'projects'"
      :newRoute="'/creator/ebook'"
    />

    <DesProjectContextMenu />
    <DesDraftContextMenu />
    <DesAudiobookContextMenu />
    <DesFlipbookContextMenu />
    <DesListCoverContextMenu />
    <DesListPresentationContextMenu />
  </div>
</template>
